import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PRIORITY_META } from "@/lib/meta";
import { fmtNZ } from "@/lib/datetime";
import { useStatuses } from "@/context/StatusContext";
import { formatDuration } from "@/components/JobTimer";
import JobTimer from "@/components/JobTimer";
import { Briefcase, Clock, CheckCircle2, PauseCircle, AlertTriangle, ChevronRight, ChevronDown } from "lucide-react";

const fmtDateTime = (iso) =>
  fmtNZ(iso, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

function StatCard({ label, value, icon: Icon, accent, testid, tint }) {
  return (
    <div
      className={`${tint || "bg-white border border-zinc-200"} rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6 hover:-translate-y-1 transition-all duration-200`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-600">
          {label}
        </div>
        <Icon size={20} className={accent} strokeWidth={2.2} />
      </div>
      <div className="font-heading text-4xl font-black tracking-tighter text-brand-display mt-4 tabular-nums">
        {value}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const canSeeTimer = user.role === "admin" || user.role === "superadmin";
  const navigate = useNavigate();
  const { metaFor, orderedKeys } = useStatuses();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [openLocs, setOpenLocs] = useState({});

  const toggleLoc = (loc) => setOpenLocs((p) => ({ ...p, [loc]: !p[loc] }));

  useEffect(() => {
    const fetchData = () => {
      api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
      api.get("/jobs").then((r) => setJobs(r.data.slice(0, 6))).catch(() => {});
    };
    fetchData();
    const id = setInterval(fetchData, 15000);
    return () => clearInterval(id);
  }, []);

  if (!stats)
    return (
      <div className="p-8 text-zinc-400 font-mono text-sm">Loading dashboard…</div>
    );

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">
          Overview
        </div>
        <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display mt-1">
          Welcome, {user.name.split(" ")[0]}
        </h1>
        <p className="text-zinc-500 mt-1 text-sm">
          {user.role === "rigger"
            ? "Here are the jobs assigned to you."
            : "Here's what's happening across your operations."}
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          label={user.role === "rigger" ? "My Jobs" : "Total Jobs"}
          value={stats.total_jobs}
          icon={Briefcase}
          accent="text-brand"
          tint="bg-brand-card-blue"
          testid="stat-total-jobs"
        />
        <StatCard
          label="In Progress"
          value={stats.by_status.in_progress}
          icon={Clock}
          accent="text-blue-600"
          tint="bg-brand-card-sage"
          testid="stat-in-progress"
        />
        <StatCard
          label="Completed"
          value={stats.by_status.processed_for_delivery}
          icon={CheckCircle2}
          accent="text-emerald-600"
          tint="bg-brand-card-cream"
          testid="stat-completed"
        />
        <StatCard
          label="Pending"
          value={stats.by_status.pending}
          icon={PauseCircle}
          accent="text-yellow-600"
          tint="bg-brand-card-blue"
          testid="stat-pending"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status breakdown */}
        <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6 lg:col-span-1">
          <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-900 mb-5">
            Status Breakdown
          </h2>
          <div className="space-y-4">
            {orderedKeys({ excludeCompleted: true }).map((s) => {
              const count = stats.by_status[s] || 0;
              const pct = stats.total_jobs ? (count / stats.total_jobs) * 100 : 0;
              return (
                <div key={s}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-semibold text-zinc-700">
                      {metaFor(s).label}
                    </span>
                    <span className="tabular-nums font-bold text-zinc-900">{count}</span>
                  </div>
                  <div className="h-2 bg-zinc-100">
                    <div
                      className="h-full bg-zinc-900 transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent jobs */}
        <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-900">
              Recent Jobs
            </h2>
            <button
              onClick={() => navigate("/jobs")}
              data-testid="view-all-jobs"
              className="text-sm font-bold text-brand hover:text-brand-dark transition-colors"
            >
              View all →
            </button>
          </div>
          {jobs.length === 0 ? (
            <div className="text-center py-10 text-zinc-400">
              <AlertTriangle className="mx-auto mb-2" size={28} />
              <p className="text-sm">No jobs yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {jobs.map((j) => (
                <button
                  key={j.id}
                  onClick={() => navigate(`/jobs/${j.id}`)}
                  data-testid={`recent-job-${j.id}`}
                  className="w-full flex items-center justify-between py-3 text-left hover:bg-zinc-50 px-2 -mx-2 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-zinc-900 truncate">{j.title}</div>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate" data-testid={`recent-job-customer-${j.id}`}>
                      <span className="font-semibold text-zinc-600">{j.customer || "No customer"}</span>
                      {" · "}
                      {j.assigned_to_name ? `Assigned to ${j.assigned_to_name}` : "Unassigned"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-full ${PRIORITY_META[j.priority].cls}`}>
                      {PRIORITY_META[j.priority].label}
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-full ${metaFor(j.status).cls}`}>
                      {metaFor(j.status).label}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Jobs by location */}
      <div className="mt-8 bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] overflow-x-auto" data-testid="jobs-by-location">
        <div className="px-5 py-4 border-b border-zinc-200">
          <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-900">
            Jobs by Location
          </h2>
          <p className="text-zinc-500 text-sm mt-0.5">Status breakdown across every site. Click a location to see its departments.</p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left">
              <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider sticky left-0 bg-white">
                Location
              </th>
              {orderedKeys().map((s) => (
                <th key={s} className="px-3 py-2 font-bold text-zinc-600 uppercase text-[10px] tracking-wider text-right whitespace-nowrap">
                  {metaFor(s).label}
                </th>
              ))}
              <th className="px-4 py-2 font-bold text-zinc-900 uppercase text-xs tracking-wider text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {stats.by_location.length === 0 ? (
              <tr>
                <td colSpan={orderedKeys().length + 2} className="px-4 py-6 text-center text-zinc-400">
                  No jobs yet.
                </td>
              </tr>
            ) : (
              stats.by_location.map((row) => {
                const depts = row.departments || [];
                const isOpen = !!openLocs[row.location];
                return (
                  <React.Fragment key={row.location}>
                    <tr
                      data-testid={`location-row-${row.location}`}
                      onClick={() => depts.length > 0 && toggleLoc(row.location)}
                      className={`hover:bg-zinc-50 ${depts.length > 0 ? "cursor-pointer" : ""}`}
                    >
                      <td className="px-4 py-2.5 font-semibold text-zinc-900 sticky left-0 bg-white">
                        <span className="inline-flex items-center gap-1.5">
                          {depts.length > 0 ? (
                            isOpen ? <ChevronDown size={14} className="text-zinc-400" /> : <ChevronRight size={14} className="text-zinc-400" />
                          ) : (
                            <span className="inline-block w-[14px]" />
                          )}
                          {row.location}
                        </span>
                      </td>
                      {orderedKeys().map((s) => {
                        const c = row.by_status[s] || 0;
                        return (
                          <td key={s} className={`px-3 py-2.5 text-right tabular-nums ${c ? "text-zinc-900 font-semibold" : "text-zinc-300"}`}>
                            {c}
                          </td>
                        );
                      })}
                      <td className="px-4 py-2.5 text-right tabular-nums font-black text-zinc-950">{row.total}</td>
                    </tr>
                    {isOpen &&
                      depts.map((d) => (
                        <tr
                          key={`${row.location}-${d.department}`}
                          data-testid={`location-dept-row-${row.location}-${d.department}`}
                          className="bg-zinc-50/70"
                        >
                          <td className="pl-11 pr-4 py-2 text-zinc-600 sticky left-0 bg-zinc-50/70">
                            <span className="inline-flex items-center gap-1.5">
                              <span className="h-1.5 w-1.5 rounded-full bg-brand/60" />
                              {d.department}
                            </span>
                          </td>
                          {orderedKeys().map((s) => {
                            const c = d.by_status[s] || 0;
                            return (
                              <td key={s} className={`px-3 py-2 text-right tabular-nums text-xs ${c ? "text-zinc-700" : "text-zinc-300"}`}>
                                {c}
                              </td>
                            );
                          })}
                          <td className="px-4 py-2 text-right tabular-nums font-bold text-zinc-800 text-xs">{d.total}</td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Job timers */}
      {canSeeTimer && (
      <div className="mt-8 bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] overflow-x-auto" data-testid="job-timers">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
          <Clock size={18} className="text-brand" />
          <div>
            <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-900">Job Timers</h2>
            <p className="text-zinc-500 text-sm mt-0.5">
              Started at <strong>In Progress</strong>, stopped at <strong>Processed for Delivery</strong>. Resumes if reopened; shows total elapsed time.
            </p>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left">
              <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Job</th>
              <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Customer</th>
              <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Started</th>
              <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Dispatched</th>
              <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider text-right">Total Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {!stats.timers || stats.timers.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-zinc-400">No jobs have started yet.</td></tr>
            ) : (
              stats.timers.map((t) => (
                <tr key={t.id} data-testid={`timer-row-${t.id}`} className="hover:bg-zinc-50">
                  <td className="px-4 py-2.5 font-semibold text-zinc-900">{t.title}</td>
                  <td className="px-4 py-2.5 text-zinc-700" data-testid={`timer-customer-${t.id}`}>{t.customer || "—"}</td>
                  <td className="px-4 py-2.5 text-zinc-700 tabular-nums text-xs">{fmtDateTime(t.started_at)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-xs">
                    {t.dispatch_at ? (
                      <span className="text-zinc-700">{fmtDateTime(t.dispatch_at)}</span>
                    ) : (
                      <span className="text-brand font-semibold">In progress…</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {t.timer_running ? (
                      <JobTimer job={t} className="justify-end" />
                    ) : (
                      <span className="tabular-nums font-bold text-zinc-950">
                        {t.timer_seconds != null ? formatDuration(t.timer_seconds) : "—"}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
